import { memo, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslate } from '../../../../../hooks/language/use-translate'
import type { LiveChat } from '../../../../../types/lives/live'
import { BadgeIcons } from './badge/badge-icons'

interface Props {
  chat: LiveChat
}

const CHARS_PER_LINE = 38
const CHAR_WIDTH_PX = 6.4
const LINE_HEIGHT_PX = 16
const BUBBLE_VERTICAL_PADDING_PX = 4
const HEADER_MIN_HEIGHT_PX = 18
const CONTENT_BOTTOM_SPACING_PX = 5
const BUBBLE_MIN_WIDTH_PX = 160
const BUBBLE_MAX_WIDTH_PX = 288
const BUBBLE_HORIZONTAL_PADDING_PX = 16
const BADGE_GAP_PX = 4
const BADGE_LEVEL_WIDTH_PX = 36
const BADGE_VIP_WIDTH_PX = 36
const BADGE_AGENCY_WIDTH_PX = 50
const BADGE_RESELLER_WIDTH_PX = 62
const BADGE_LUCKY_HERO_WIDTH_PX = 46
const BADGE_SUPER_RICH_WIDTH_PX = 56
const USERNAME_MARGIN_LEFT_PX = 4
const USERNAME_AFTER_SPACE_PX = 4
const AVATAR_SIZE_PX = 24
const BUBBLE_STYLE_PRESETS = {
  oneLine: {
    minHeight: 34,
    padding: '3px 8px 5px',
  },
  twoLines: {
    minHeight: 48,
    padding: '4px 8px 7px',
  },
  multiLine: {
    minHeight: 62,
    padding: '5px 8px 8px',
  },
} as const

interface InlinePrefixEstimateParams {
  username?: string
  level?: string | number
  vipId?: number
  agencyLevel?: number
  resellerLevel?: number
  luckyHero?: boolean
  superRich?: boolean
}

function estimateLines(text: string) {
  if (!text) {
    return 1
  }

  return text.split('\n').reduce((total, line) => {
    return total + Math.max(1, Math.ceil(line.length / CHARS_PER_LINE))
  }, 0)
}

function getEstimatedLineHeight() {
  return LINE_HEIGHT_PX
}

function estimateBubbleWidth(text: string) {
  if (!text) {
    return BUBBLE_MIN_WIDTH_PX
  }

  const longestLine = text.split('\n').reduce((max, line) => {
    return Math.max(max, line.length)
  }, 0)

  const estimatedWidth = longestLine * CHAR_WIDTH_PX + 16

  return Math.min(
    BUBBLE_MAX_WIDTH_PX,
    Math.max(BUBBLE_MIN_WIDTH_PX, estimatedWidth),
  )
}

function estimateBadgeWidth({
  level,
  vipId,
  agencyLevel,
  resellerLevel,
  luckyHero,
  superRich,
}: Omit<InlinePrefixEstimateParams, 'username'>) {
  const widths = [
    level ? BADGE_LEVEL_WIDTH_PX : 0,
    vipId ? BADGE_VIP_WIDTH_PX : 0,
    agencyLevel ? BADGE_AGENCY_WIDTH_PX : 0,
    resellerLevel ? BADGE_RESELLER_WIDTH_PX : 0,
    luckyHero ? BADGE_LUCKY_HERO_WIDTH_PX : 0,
    superRich ? BADGE_SUPER_RICH_WIDTH_PX : 0,
  ].filter(Boolean)

  if (!widths.length) {
    return 0
  }

  return (
    widths.reduce((total, width) => total + width, 0) +
    BADGE_GAP_PX * (widths.length - 1)
  )
}

function estimateInlinePrefixWidth({
  username,
  level,
  vipId,
  agencyLevel,
  resellerLevel,
  luckyHero,
  superRich,
}: InlinePrefixEstimateParams) {
  const badgeWidth = estimateBadgeWidth({
    level,
    vipId,
    agencyLevel,
    resellerLevel,
    luckyHero,
    superRich,
  })

  const usernameWidth = username
    ? username.length * CHAR_WIDTH_PX +
      USERNAME_MARGIN_LEFT_PX +
      USERNAME_AFTER_SPACE_PX
    : 0

  return badgeWidth + usernameWidth
}

function estimateInlineMessageLines(
  text: string,
  prefixWidthPx: number,
  bubbleWidthPx: number,
) {
  if (!text) {
    return 1
  }

  const contentWidthPx = bubbleWidthPx - BUBBLE_HORIZONTAL_PADDING_PX
  const safeContentWidthPx = Math.max(contentWidthPx, BUBBLE_MIN_WIDTH_PX - 24)
  const firstLineWidthPx = Math.max(
    CHAR_WIDTH_PX * 6,
    safeContentWidthPx - prefixWidthPx,
  )

  return text.split('\n').reduce((total, rawLine) => {
    const line = rawLine || ' '
    const lineWidthPx = line.length * CHAR_WIDTH_PX

    if (lineWidthPx <= firstLineWidthPx) {
      return total + 1
    }

    const remainingWidthPx = lineWidthPx - firstLineWidthPx
    const extraLines = Math.ceil(remainingWidthPx / safeContentWidthPx)

    return total + 1 + extraLines
  }, 0)
}

function resolveBubblePreset(lines: number) {
  if (lines <= 1) {
    return BUBBLE_STYLE_PRESETS.oneLine
  }

  if (lines === 2) {
    return BUBBLE_STYLE_PRESETS.twoLines
  }

  return BUBBLE_STYLE_PRESETS.multiLine
}

export const Message = memo(({ chat }: Props) => {
  const { translate } = useTranslate()
  const contentRef = useRef<HTMLDivElement | null>(null)
  const [measuredContentHeight, setMeasuredContentHeight] = useState(0)

  const senderUsername =
    chat?.user?.username ||
    chat?.message?.userSentName ||
    chat?.message?.username

  const username = chat.isInvisible
    ? translate('invisible')
    : senderUsername || ''

  const messageType = chat?.message?.type
  const avatarUrl = chat?.user?.imgUser

  const handleClickName = () => {
    if ('ReactNativeWebView' in window) {
      // @ts-ignore
      window.ReactNativeWebView.postMessage(chat?.user?.id)
    }
  }

  const badges = (
    <BadgeIcons
      level={chat?.level?.level}
      vipId={chat?.userVip?.vipId}
      agencyLevel={chat?.agencyBadges}
      resellerLevel={chat?.resellerBadges}
      luckyHero={chat?.luckyHero}
      superRich={chat?.superRich}
    />
  )

  const sender = username ? (
    <span
      onClick={handleClickName}
      className="ml-1 text-sm font-normal leading-5 text-blue-500 cursor-pointer break-all align-baseline"
      style={{ fontFamily: 'Nunito, sans-serif' }}
    >
      {username}
    </span>
  ) : null

  const inlinePrefixWidth = useMemo(
    () =>
      estimateInlinePrefixWidth({
        username,
        level: chat?.level?.level,
        vipId: chat?.userVip?.vipId,
        agencyLevel: chat?.agencyBadges,
        resellerLevel: chat?.resellerBadges,
        luckyHero: chat?.luckyHero,
        superRich: chat?.superRich,
      }),
    [
      chat?.agencyBadges,
      chat?.level?.level,
      chat?.luckyHero,
      chat?.resellerBadges,
      chat?.superRich,
      chat?.userVip?.vipId,
      username,
    ],
  )

  const bubbleMinHeight = useMemo(() => {
    if (messageType === 'game-old-miner') {
      const content =
        chat?.message?.message ||
        [
          chat?.message?.username,
          translate('chat-live-won'),
          chat?.message?.giftName,
          `${chat?.message?.quantity ?? ''}x${chat?.message?.valueGift ?? ''}`,
          translate('chat-live-old-miner'),
        ]
          .filter(Boolean)
          .join(' ')

      const lines = estimateLines(content)

      return (
        BUBBLE_VERTICAL_PADDING_PX +
        lines * getEstimatedLineHeight() +
        CONTENT_BOTTOM_SPACING_PX
      )
    }

    if (messageType === 'gift') {
      const content = [
        'enviou para',
        chat?.message?.userReceivedNames,
        'o presente',
        chat?.message?.giftName,
        `(${chat?.message?.quantity ?? ''}x ${chat?.message?.valueGift ?? ''})`,
      ]
        .filter(Boolean)
        .join(' ')

      const lines = estimateLines(content)

      return (
        BUBBLE_VERTICAL_PADDING_PX +
        HEADER_MIN_HEIGHT_PX +
        lines * getEstimatedLineHeight() +
        CONTENT_BOTTOM_SPACING_PX
      )
    }

    const content = chat?.message?.returnTimes
      ? [
          chat?.message?.username,
          'ganhou',
          `${chat?.message?.returnTimes ?? ''}x (${chat?.message?.returnValues ?? ''})`,
          'no presente da sorte',
          chat?.message?.giftName,
        ]
          .filter(Boolean)
          .join(' ')
      : chat?.message?.message || ''

    const lines =
      !chat?.message?.returnTimes && messageType !== 'gift'
        ? estimateInlineMessageLines(
            content,
            inlinePrefixWidth,
            BUBBLE_MAX_WIDTH_PX,
          )
        : estimateLines(content)

    return (
      BUBBLE_VERTICAL_PADDING_PX +
      HEADER_MIN_HEIGHT_PX +
      lines * getEstimatedLineHeight() +
      CONTENT_BOTTOM_SPACING_PX
    )
  }, [chat, inlinePrefixWidth, messageType, translate])

  const estimatedLineCount = useMemo(() => {
    if (messageType === 'game-old-miner') {
      const content =
        chat?.message?.message ||
        [
          chat?.message?.username,
          translate('chat-live-won'),
          chat?.message?.giftName,
          `${chat?.message?.quantity ?? ''}x${chat?.message?.valueGift ?? ''}`,
          translate('chat-live-old-miner'),
        ]
          .filter(Boolean)
          .join(' ')

      return estimateLines(content)
    }

    if (messageType === 'gift') {
      const content = [
        'enviou para',
        chat?.message?.userReceivedNames,
        'o presente',
        chat?.message?.giftName,
        `(${chat?.message?.quantity ?? ''}x ${chat?.message?.valueGift ?? ''})`,
      ]
        .filter(Boolean)
        .join(' ')

      return estimateLines(content)
    }

    const content = chat?.message?.returnTimes
      ? [
          chat?.message?.username,
          'ganhou',
          `${chat?.message?.returnTimes ?? ''}x (${chat?.message?.returnValues ?? ''})`,
          'no presente da sorte',
          chat?.message?.giftName,
        ]
          .filter(Boolean)
          .join(' ')
      : chat?.message?.message || ''

    return !chat?.message?.returnTimes && messageType !== 'gift'
      ? estimateInlineMessageLines(
          content,
          inlinePrefixWidth,
          BUBBLE_MAX_WIDTH_PX,
        )
      : estimateLines(content)
  }, [chat, inlinePrefixWidth, messageType, translate])

  useLayoutEffect(() => {
    const contentElement = contentRef.current

    if (!contentElement) {
      return
    }

    const measure = () => {
      setMeasuredContentHeight(contentElement.getBoundingClientRect().height)
    }

    measure()

    if (typeof ResizeObserver === 'undefined') {
      return
    }

    const observer = new ResizeObserver(() => {
      measure()
    })

    observer.observe(contentElement)

    return () => {
      observer.disconnect()
    }
  }, [chat])

  const measuredBubbleMinHeight = useMemo(() => {
    const preset = resolveBubblePreset(estimatedLineCount)

    if (!measuredContentHeight) {
      return Math.max(preset.minHeight, bubbleMinHeight)
    }

    return Math.max(
      preset.minHeight,
      bubbleMinHeight,
      Math.ceil(
        measuredContentHeight +
          BUBBLE_VERTICAL_PADDING_PX +
          CONTENT_BOTTOM_SPACING_PX +
          8,
      ),
    )
  }, [bubbleMinHeight, estimatedLineCount, measuredContentHeight])

  const bubblePreset = useMemo(
    () => resolveBubblePreset(estimatedLineCount),
    [estimatedLineCount],
  )
  const isSingleLine = estimatedLineCount <= 1
  const isTwoLines = estimatedLineCount === 2

  const bubbleWidth = useMemo(() => {
    if (messageType !== 'game-old-miner') {
      return BUBBLE_MAX_WIDTH_PX
    }

    const content =
      chat?.message?.message ||
      [
        chat?.message?.username,
        translate('chat-live-won'),
        chat?.message?.giftName,
        `${chat?.message?.quantity ?? ''}x${chat?.message?.valueGift ?? ''}`,
        translate('chat-live-old-miner'),
      ]
        .filter(Boolean)
        .join(' ')

    return estimateBubbleWidth(content)
  }, [chat, messageType, translate])

  return (
    <div
      className={`flex flex-col rounded-lg ${isSingleLine ? 'justify-center' : ''}`}
      style={{
        backgroundColor: 'rgba(20, 20, 20, 0.92)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        overflowWrap: 'anywhere',
        minHeight: `${measuredBubbleMinHeight}px`,
        width: `${bubbleWidth}px`,
        maxWidth: `${BUBBLE_MAX_WIDTH_PX}px`,
        padding: bubblePreset.padding,
      }}
      key={chat.id}
    >
      <div
        ref={contentRef}
        className={`flex gap-2 ${isSingleLine ? 'items-center' : 'items-start pt-1'}`}
      >
        <div
          className={`shrink-0 overflow-hidden rounded-full bg-white/10 ${isSingleLine ? '' : isTwoLines ? 'self-center' : 'mt-0.5'}`}
          style={{
            width: `${AVATAR_SIZE_PX}px`,
            height: `${AVATAR_SIZE_PX}px`,
          }}
        >
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={username || 'user avatar'}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-[10px] font-bold uppercase text-white/80">
              {username?.slice(0, 1) || '?'}
            </div>
          )}
        </div>

        <div className={`min-w-0 flex-1 ${isTwoLines ? 'pt-1' : ''}`}>
          {messageType === 'game-old-miner' && (
            <div className="text-sm leading-5 break-words pb-0.5">
              <span className="text-blue-500">{chat?.message?.username} </span>
              <span>{translate('chat-live-won')} </span>

              <span className="text-amber-300 font-bold">
                {chat?.message?.giftName}{' '}
              </span>

              <span>
                {chat?.message?.quantity}x{chat?.message?.valueGift}
              </span>

              <span> {translate('chat-live-old-miner')}</span>
            </div>
          )}

          {messageType === 'gift' && (
            <div className="flex flex-col gap-0.5 pb-0.5">
              <div
                className="text-sm font-normal leading-5 break-words"
                style={{ fontFamily: 'Nunito, sans-serif' }}
              >
                {badges}
                {sender}
                {sender && <span> </span>}
                <span>enviou para </span>
                <span className="text-amber-300 font-bold break-all">
                  {chat?.message?.userReceivedNames}
                </span>
                <span> o presente </span>
                <span className="text-amber-300 font-bold break-all">
                  {chat?.message?.giftName}
                </span>
                <span>
                  {' '}
                  ({chat?.message?.quantity}x {chat?.message?.valueGift})
                </span>
              </div>
            </div>
          )}

          {messageType !== 'game-old-miner' && messageType !== 'gift' && (
            <div className="flex flex-col gap-0.5 break-words pb-0.5">
              {!chat?.message?.returnTimes && (
                <div
                  className="text-sm font-normal leading-5 break-words"
                  style={{ fontFamily: 'Nunito, sans-serif' }}
                >
                  {badges}
                  {sender}
                  {sender && <span> </span>}
                  <span className="break-words align-baseline">
                    {chat?.message?.message}
                  </span>
                </div>
              )}
              {chat?.message?.returnTimes && (
                <span className="text-sm leading-5 break-words">
                  <span className="text-blue-500">
                    {chat?.message?.username}
                  </span>{' '}
                  ganhou{' '}
                  <span className="text-amber-300">
                    ganhou {chat?.message?.returnTimes}x (
                    {chat?.message?.returnValues})
                  </span>{' '}
                  no presente da sorte
                  <span className="text-amber-300">
                    {' '}
                    {chat?.message?.giftName}
                  </span>
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
})
